/**
 * Locale-aware formatting helpers for money and percentage values coming from
 * the backend. Centralised here so any page that needs to render a
 * configured fee/rate (platform fee, credit price, etc.) formats it
 * consistently and respects the active i18n locale, instead of re-deriving
 * ad-hoc `Intl.NumberFormat` calls per component.
 */

/**
 * Format an integer amount expressed in minor currency units (cents) as a
 * localized currency string, e.g. `formatCentsAsCurrency(50, 'eur', 'en')`
 * -> "€0.50", and with locale `'nl'` -> "€ 0,50".
 *
 * `currency` must be a lowercase or uppercase ISO 4217 code (as returned by
 * the API) - never hardcode a currency symbol or code on the caller side.
 */
export function formatCentsAsCurrency(cents: number, currency: string, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100)
}

/**
 * Format a percentage value (already expressed as a percentage, e.g. `2` for
 * 2%, not as a fraction) without trailing zeros for whole numbers, while
 * preserving genuine fractional precision - `2` -> "2%", `3.5` -> "3.5%"
 * (locale-formatted, e.g. "3,5%" for `nl`/`fr`).
 */
export function formatPercentage(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: 2,
  }).format(value / 100)
}
